import { TextInput, CustomSelect, ErrorMessage, RadioGroup } from '@/shared/ui/components';
import {
  FREQUENTLY_OF_SYMPTOMS_OPTIONS,
  INSURANCE_TYPE_OPTIONS,
  PATIENT_SYMPTOMS_OPTIONS,
  PRE_AUTHORIZATION_OPTIONS,
  USED_HEART_MONITOR_OPTIONS,
} from '@/shared/config';
import { clearIcon } from '@/shared/ui/assets';
import { isWebViewCreateStudy } from '@/shared/lib';
import { Select } from 'antd';
import { forwardRef } from 'react';
import { t } from 'ttag';
import { useNewStudyStep4 } from './useNewStudyStep4';
import NewStudyStepSkeleton from '../newStudyStepSkeleton/newStudyStepSkeleton';
import type { NewStudyStep4Props } from './newStudyStep4.types';
import './newStudyStep4.scss';

/**
 * Renders step 4 of the new-study wizard: primary and secondary diagnosis code search, patient medication and physician amendment notes, insurance details, and the patient symptom history. Pulls state and handlers from useNewStudyStep4, shows a step skeleton until isLoadedData is true, adds a web-view sub-header when running inside the create-study web view, and surfaces per-field messages from state.errorObject under each control.
 *
 * @param props - Step data, the isLoadedData flag and the wizard callbacks.
 * @param ref - Forwarded ref through which the wizard drives validation and submission.
 * @returns The diagnosis, insurance and medical history step markup.
 */
const NewStudyStep4 = forwardRef((props: NewStudyStep4Props, ref) => {
  const {
    state,
    onChangePatientSymptoms,
    onChange,
    onChangeSelection,
    handleSearchSelectPrimary,
    handleSearchSelectSecond,
    handlePopupScrollPrimary,
    handlePopupScrollSecond,
  } = useNewStudyStep4(props, ref);

  const isWebView = isWebViewCreateStudy();

  if (!props.isLoadedData) {
    return <NewStudyStepSkeleton />;
  }

  return (
    <div className="step-content step-4">
      <div className="centered-content">
        <div>
          <div className="centered-content__header">{t`Diagnosis`}</div>
          {isWebView && <div className="sub-header">{t`Diagnosis`}</div>}
          <div className="centered-content__body">
            <div className="diagnosis-code-select">
              <div className="diagnosis-code-select__label">{t`Primary diagnosis code`}</div>
              <Select
                id="primary-diagnosis-code"
                className="custom-select-test"
                style={{ width: '100%' }}
                name="primary"
                showSearch
                onSearch={handleSearchSelectPrimary}
                placeholder={t`Search & select a code`}
                options={state.diagnosisCodesPrimary}
                onChange={(value) => {
                  onChange('primary', value);
                }}
                onPopupScroll={handlePopupScrollPrimary as (e: any) => void}
                size="middle"
                filterOption={false}
                value={state.newStudyStep4Data.primary}
                status={state.errorObject.primary ? 'error' : ''}
                getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                loading={state.isLoadingPrimary}
                aria-label={t`Primary diagnosis code`}
                notFoundContent={
                  <div className="empty-select">
                    {state.isLoadingPrimary
                      ? `${t`Loading`}...`
                      : state.searchPrimary
                        ? t`No diagnosis code matches your search`
                        : t`Search by code or diagnosis name`}
                  </div>
                }
              />
              {!!state.errorObject.primary && (
                <ErrorMessage errorMessage={state.errorObject.primary} hasIcon />
              )}
            </div>
            <div className="diagnosis-code-select">
              <div className="diagnosis-code-select__label">
                {t`Secondary diagnosis code`} <span className="optional-label">(optional)</span>
              </div>
              <Select
                id="secondary-diagnosis-code"
                className="custom-select-test"
                style={{ width: '100%' }}
                name="second"
                showSearch
                onSearch={handleSearchSelectSecond}
                placeholder={t`Search & select a code`}
                options={state.diagnosisCodesSecond}
                onChange={(value) => {
                  onChange('second', value);
                }}
                onPopupScroll={handlePopupScrollSecond as (e: any) => void}
                size="middle"
                filterOption={false}
                value={state.newStudyStep4Data.second}
                status={state.errorObject.second ? 'error' : ''}
                getPopupContainer={(trigger) => trigger.parentNode as HTMLElement}
                loading={state.isLoadingSecond}
                aria-label={t`Secondary diagnosis code`}
                notFoundContent={
                  <div className="empty-select">
                    {state.isLoadingSecond
                      ? `${t`Loading`}...`
                      : state.searchSecond
                        ? t`No diagnosis code matches your search`
                        : t`Search by code or diagnosis name`}
                  </div>
                }
                allowClear={{
                  clearIcon: <img src={clearIcon} alt="Clear icon" />,
                }}
              />
              {!!state.errorObject.second && (
                <ErrorMessage errorMessage={state.errorObject.second} hasIcon />
              )}
            </div>
            <TextInput
              id="patient-medication"
              className="space-top-20 has-default-height"
              name="patientMedications"
              type="textarea"
              label={
                <div>
                  {t`Patient medication`} <span className="optional-label">(optional)</span>
                </div>
              }
              placeholder={t`Note...`}
              value={state.newStudyStep4Data.patientMedications}
              onChange={onChange}
              isError={!!state.errorObject.patientMedications}
              errorMessage={state.errorObject.patientMedications}
            />
            <TextInput
              id="physician-amendment-notes"
              className="space-top-20 has-default-height"
              name="physiciansNote"
              type="textarea"
              label={
                <div>
                  {t`Physician amendment notes`} <span className="optional-label">(optional)</span>
                </div>
              }
              placeholder={t`Note...`}
              value={state.newStudyStep4Data.physiciansNote}
              onChange={onChange}
              isError={!!state.errorObject.physiciansNote}
              errorMessage={state.errorObject.physiciansNote}
            />
          </div>
        </div>
        <div className="insurance-input space-top-20">
          <div className={isWebView ? 'sub-header' : 'centered-content__header'}>
            {t`Insurance`}
          </div>
          <div className="centered-content__body">
            <CustomSelect
              id="insurance-type"
              title={t`Insurance type`}
              aria-label={t`Insurance type`}
              showSearch
              allowClear={false}
              name="insuranceType"
              size="middle"
              placeholder={t`Select a insurance`}
              options={INSURANCE_TYPE_OPTIONS}
              value={state.insuranceType}
              onChange={(name, value, option) =>
                onChangeSelection(name, value, option as { label: string; value: string })
              }
              isError={!!state.errorObject.insuranceType}
              errorMessage={state.errorObject.insuranceType}
            />
            <TextInput
              id="insurance-provider"
              className="space-top-20"
              name="insuranceProvider"
              label={
                <div>
                  {t`Provider`} <span className="optional-label">(optional)</span>
                </div>
              }
              placeholder={t`Enter provider`}
              value={state.newStudyStep4Data.insuranceProvider}
              onChange={onChange}
            />
            <div className="w-100 mt4">
              <span>{t`Pre-authorization required`}</span>
              <div className="flex mt2">
                <RadioGroup
                  className="radio-input"
                  value={state.newStudyStep4Data.insurancePreAuth}
                  name="insurancePreAuth"
                  onChange={onChange}
                  options={PRE_AUTHORIZATION_OPTIONS}
                  size="small"
                />
              </div>
            </div>
          </div>
        </div>
        <div className="mt-40 part-3">
          <div className={isWebView ? 'sub-header' : 'centered-content__header'}>
            {t`Medical history`}
          </div>
          <div className="centered-content__header-description">
            {t`Medical history of the patient in the last 12 months`}
          </div>
          <div className="centered-content__body">
            <CustomSelect
              id="patient-symptoms"
              mode="multiple"
              title={t`Symptoms the patient experienced`}
              aria-label={t`Symptoms the patient experienced`}
              showSearch
              allowClear={false}
              name="patientSymptoms"
              size="middle"
              placeholder={t`No symptom`}
              options={PATIENT_SYMPTOMS_OPTIONS}
              value={state.patientSymptoms}
              onChange={(name, value, options) =>
                onChangePatientSymptoms(name, value, options as { label: string; value: string }[])
              }
            />
            {!!state.newStudyStep4Data.patientSymptoms?.length && (
              <>
                <CustomSelect
                  id="frequently-of-symptoms"
                  className="space-top-20"
                  title={t`How many times did the symptoms happen?`}
                  aria-label={t`How many times did the symptoms happen?`}
                  showSearch
                  allowClear={false}
                  name="frequentlyOfSymptoms"
                  size="middle"
                  placeholder={t`Select a range`}
                  options={FREQUENTLY_OF_SYMPTOMS_OPTIONS}
                  value={state.frequentlyOfSymptoms}
                  onChange={(name, value, option) =>
                    onChangeSelection(name, value, option as { label: string; value: string })
                  }
                  isError={!!state.errorObject.frequentlyOfSymptoms}
                  errorMessage={state.errorObject.frequentlyOfSymptoms}
                />
                <div className="w-100 space-top-20">
                  <div className="flex mt2">
                    <RadioGroup
                      title={t`Had a heart monitor before?`}
                      className="radio-input"
                      value={state.newStudyStep4Data.usedHeartMonitor}
                      name="usedHeartMonitor"
                      onChange={onChange}
                      options={USED_HEART_MONITOR_OPTIONS as any}
                      size="small"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
});

export default NewStudyStep4;
